import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ShareMenu from "@/components/ShareMenu";
import { toast } from "sonner";
import { Sparkles, Loader2, Upload, Download, X, Coins, Share2, Star, ChevronDown } from "lucide-react";
import { zoomCard } from "@/components/CardZoom";

export default function SportsCardStudio() {
  const { user, refresh } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [cards, setCards] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [featBusy, setFeatBusy] = useState(false);
  const [cardType, setCardType] = useState("game");
  const [welcomeStyle, setWelcomeStyle] = useState("");
  const [gender, setGender] = useState("");
  const fileRef = useRef(null);
  const isFeatured = !!(viewer && user?.featured_card && user.featured_card === viewer.url);

  const toggleFeature = async () => {
    setFeatBusy(true);
    try {
      await api.post(`/sports-card/${viewer.id}/feature`, { enabled: !isFeatured });
      await refresh();
      toast.success(!isFeatured ? "This card is now your Profile Card!" : "Switched back to your regular player card");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFeatBusy(false); }
  };

  const share = async () => {
    setSharing(true);
    try {
      await api.post("/community/posts", {
        content: "Just minted my new AI Sports Card in the Bullpen! 🔥🏓",
        image: viewer.url, type: "post",
      });
      toast.success("Card shared to the Community feed!");
      setViewer(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSharing(false); }
  };

  useEffect(() => {
    api.get("/sports-card/pricing").then(({ data }) => setPricing(data)).catch(() => {});
    api.get("/sports-cards/mine").then(({ data }) => setCards(data)).catch(() => {});
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 1024 / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setPhoto(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sports-card/generate",
        { image: photo, card_type: cardType, welcome_style: !isLevel ? welcomeStyle : "", gender }, { timeout: 150000 });
      setCards((c) => [data, ...c]);
      setPhoto(null);
      setViewer(data);
      api.get("/sports-card/pricing").then(({ data: p }) => setPricing(p)).catch(() => {});
      toast.success(data.cost > 0 ? `Sports card minted — ${data.cost} BLC` : "Sports card minted — FREE with your tier!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Card generation failed — try again"); }
    finally { setBusy(false); }
  };

  if (!pricing) return null;
  const lv = pricing.level || {};
  const g = pricing.game || {};
  const isLevel = cardType === "level";
  const isGame = cardType === "game";
  const selCost = isLevel ? lv.cost : isGame ? g.cost : pricing.cost;
  const free = selCost <= 0;
  const welcomeStyles = pricing.welcome_styles || [];
  const premiumStyles = pricing.premium_styles || [];
  const styleFree = welcomeStyles.length > 0 && isGame;
  const styles = styleFree ? welcomeStyles : premiumStyles;

  return (
    <div className="bg-pb-surface border border-pb-border" data-testid="sports-card-studio">
      <button data-testid="sports-card-studio-toggle" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-6 py-4 text-left hover:bg-pb-elevated/40 transition-colors">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-pb-red" />
          <span className="font-display font-bold text-lg uppercase">AI Sports Card</span>
          {cards.length > 0 && <span className="text-[9px] uppercase tracking-widest text-zinc-500">{cards.length} minted</span>}
        </div>
        <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
          {open ? "Hide" : "Create & View Cards"}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
      <div className="px-6 pb-6" data-testid="sports-card-studio-body">
      <div className="flex flex-wrap items-center justify-end gap-2 mb-1">
        <span data-testid="sports-card-cost"
          className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 border ${
            free ? "border-amber-400/50 text-amber-400 bg-amber-400/10" : "border-pb-red/50 text-pb-red bg-pb-red/10"}`}>
          <Coins className="w-3 h-3" /> {isGame
            ? (g.free ? "YOUR FIRST GAME CARD IS FREE" : `${g.cost} BLC — re-mint as you level up`)
            : isLevel
            ? `${lv.cost} BLC per card`
            : (free ? `FREE — ${pricing.tier} perk` : `${pricing.cost} BLC per card`)}
        </span>
      </div>
      <div className="flex flex-wrap gap-2 mb-3 mt-2">
        <button data-testid="card-type-game" onClick={() => setCardType("game")}
          className={`text-left px-3 py-2 border transition-colors ${isGame ? "border-yellow-400 bg-yellow-400/10" : "border-pb-border hover:border-zinc-500"}`}>
          <div className="text-[10px] font-black uppercase flex items-center gap-1.5 text-yellow-400">Game Player Card
            {g.rc && <span className="text-[7px] font-black px-1 py-0.5 bg-red-600 text-white" data-testid="game-rc-chip">RC</span>}
          </div>
          <div className="text-[8px] uppercase tracking-widest text-zinc-500">Card Wars battle stats — {g.division}</div>
        </button>
        <button data-testid="card-type-tier" onClick={() => setCardType("tier")}
          className={`text-left px-3 py-2 border transition-colors ${cardType === "tier" ? "border-pb-red bg-pb-red/10" : "border-pb-border hover:border-zinc-500"}`}>
          <div className="text-[10px] font-black uppercase">Tier Card</div>
          <div className="text-[8px] uppercase tracking-widest text-zinc-500">{pricing.rarity_label || "Standard silver frame"}</div>
        </button>
        <button data-testid="card-type-level" onClick={() => setCardType("level")}
          className={`text-left px-3 py-2 border transition-colors ${isLevel ? "border-sky-400 bg-sky-400/10" : "border-pb-border hover:border-zinc-500"}`}>
          <div className="text-[10px] font-black uppercase flex items-center gap-1.5">Level Card
            {lv.rc && <span className="text-[7px] font-black px-1 py-0.5 bg-red-600 text-white" data-testid="level-rc-chip">RC</span>}
          </div>
          <div className="text-[8px] uppercase tracking-widest text-sky-300">{lv.gem_label} — {lv.division}</div>
        </button>
      </div>
      {isGame && g.stats && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="game-stats-preview">
          <span className="text-[8px] font-black uppercase tracking-[0.2em] text-zinc-500">Your battle stats:</span>
          {["dink", "power", "speed", "iq", "clutch"].map((s) => (
            <span key={s} className={`text-[9px] font-mono font-bold px-1.5 py-0.5 border ${s === "clutch" ? "border-yellow-400/60 text-yellow-400" : "border-pb-border text-zinc-300"}`}>
              {s.toUpperCase()} {g.stats[s]}
            </span>
          ))}
          <span className="text-[8px] uppercase tracking-widest text-zinc-600">stamped in gold on the card</span>
        </div>
      )}
      {isGame && g.rc && (
        <div className="text-[10px] text-yellow-300 mb-2" data-testid="game-rc-explainer">
          Your {g.division} Game Card gets the collector RC (Rookie Card) badge — as your level climbs, re-mint stronger cards and these early ones become the chase.
        </div>
      )}
      {isLevel && lv.rc && (
        <div className="text-[10px] text-sky-300 mb-2" data-testid="rc-explainer">
          Your {lv.division} card gets the collector RC (Rookie Card) badge — as your level climbs, these early cards become the ones collectors chase.
        </div>
      )}
      <p className="text-xs text-zinc-500 mb-5">
        Upload an action photo and our AI turns you into a collectible trading card with your live Bullpen stats baked in.
        {!free && " Bonus coins are spent first."}
      </p>

      {!isLevel && styles.length > 0 && (
        <div className="border border-fuchsia-400/40 bg-fuchsia-400/5 p-3 mb-4" data-testid="welcome-style-picker">
          <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-300 mb-0.5">
            {styleFree ? "🎁 24-Hour Welcome Exclusive — pick a legendary border" : "✨ Premium Borders — priced by rarity tier"}
          </div>
          <p className="text-[10px] text-zinc-500 mb-2">
            {styleFree
              ? "For your first card only, you get the same premium AI styles the Bullpen uses on its exclusive drops. This unlocks once — right now."
              : "The same premium AI styles the Bullpen forges its exclusive drops with — added to your mint price."}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button data-testid="welcome-style-none" onClick={() => setWelcomeStyle("")}
              className={`text-left px-2.5 py-2 border transition-colors ${!welcomeStyle ? "border-zinc-300 bg-white/5 text-zinc-200" : "border-pb-border text-zinc-500 hover:border-zinc-500"}`}>
              <div className="text-[10px] font-black uppercase">Classic</div>
              <div className="text-[8px] uppercase tracking-widest opacity-60">Standard frame</div>
            </button>
            {styles.map((s) => (
              <button key={s.id} data-testid={`welcome-style-${s.id}`} onClick={() => setWelcomeStyle(s.id)}
                className={`text-left px-2.5 py-2 border transition-colors ${welcomeStyle === s.id ? "border-fuchsia-400 bg-fuchsia-400/10 text-fuchsia-200" : "border-pb-border text-zinc-500 hover:border-zinc-500"}`}>
                <div className="text-[10px] font-black uppercase">{s.id === "prism" ? "Prism" : s.id === "galaxy" ? "Galaxy" : "Comic Hero"}</div>
                <div className="text-[8px] uppercase tracking-widest opacity-60">{s.label}{!styleFree && s.price != null ? ` · +${s.price} BLC` : ""}</div>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="border border-sky-400/40 bg-sky-400/5 p-3 mb-4" data-testid="card-gender-picker">
        <div className="text-[10px] font-black uppercase tracking-widest text-sky-300 mb-0.5">⚔ Card Wars Gender — required to render</div>
        <p className="text-[10px] text-zinc-500 mb-2">
          Your card battles in gendered doubles (Men's / Women's / Mixed) in Card Wars — tag it so it can take the court.
        </p>
        <div className="flex gap-2">
          <button data-testid="card-gender-male" onClick={() => setGender("male")}
            className={`flex-1 sm:flex-none sm:px-8 py-2.5 border font-black uppercase tracking-[0.2em] text-xs transition-all ${gender === "male"
              ? "border-sky-400 bg-sky-400/15 text-sky-200 shadow-[0_0_14px_rgba(56,189,248,.35)]"
              : "border-pb-border text-zinc-500 hover:border-sky-400/60 hover:text-sky-300"}`}>
            ♂ Male
          </button>
          <button data-testid="card-gender-female" onClick={() => setGender("female")}
            className={`flex-1 sm:flex-none sm:px-8 py-2.5 border font-black uppercase tracking-[0.2em] text-xs transition-all ${gender === "female"
              ? "border-pink-400 bg-pink-400/15 text-pink-200 shadow-[0_0_14px_rgba(244,114,182,.35)]"
              : "border-pb-border text-zinc-500 hover:border-pink-400/60 hover:text-pink-300"}`}>
            ♀ Female
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-start gap-5 mb-5">
        {photo ? (          <div className="relative">
            <img src={photo} alt="Your upload" className="w-32 h-40 object-cover border border-pb-border" data-testid="sports-card-preview" />
            <button data-testid="sports-card-clear-btn" onClick={() => setPhoto(null)}
              className="absolute -top-2 -right-2 bg-black border border-pb-border p-1 hover:border-red-500">
              <X className="w-3 h-3 text-zinc-300" />
            </button>
          </div>
        ) : (
          <button data-testid="sports-card-upload-btn" onClick={() => fileRef.current?.click()}
            className="w-32 h-40 border border-dashed border-pb-border hover:border-pb-red flex flex-col items-center justify-center gap-2 text-zinc-500 hover:text-pb-red transition-colors">
            <Upload className="w-5 h-5" />
            <span className="text-[9px] uppercase tracking-widest font-bold text-center px-2">Upload action photo</span>
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="sports-card-file-input" />
        <div className="flex-1 min-w-[180px]">
          <button data-testid="sports-card-generate-btn" onClick={generate} disabled={!photo || !gender || busy}
            className="flex items-center gap-2 bg-pb-red text-black font-black uppercase tracking-widest px-6 py-3 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? "Designing your card…" : "Generate my card"}
          </button>
          {!gender && photo && <p className="text-[10px] text-sky-300 mt-2" data-testid="gender-required-hint">Pick Male or Female above — your card can't render without it.</p>}
          {busy && <p className="text-[10px] text-zinc-500 mt-2 animate-pulse">The AI is cutting you out, relighting the shot and stamping your stats — ~20-30 seconds.</p>}
        </div>
      </div>

      {cards.length > 0 && (
        <div data-testid="sports-card-gallery">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">My Card Collection ({cards.length})</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {cards.map((c) => (
              <button key={c.id} data-testid={`sports-card-item-${c.id}`} onClick={() => setViewer(c)}
                className="border border-pb-border hover:border-pb-red transition-colors overflow-hidden">
                <img src={c.url} alt="Sports card" className="w-full aspect-[3/4] object-contain bg-black cursor-zoom-in" loading="lazy"
                  title="Tap to zoom" onClick={() => zoomCard(c.url)} />
              </button>
            ))}
          </div>
        </div>
      )}

      {viewer && (
        <div className="fixed inset-0 z-[85] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setViewer(null)}>
          <div className="max-w-sm w-full animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="sports-card-viewer">
            <img src={viewer.url} alt="Sports card" className="w-full border border-pb-border" />
            <div className={`flex items-center justify-between gap-3 mt-3 border px-3.5 py-2.5 ${isFeatured ? "border-amber-400/60 bg-amber-400/10" : "border-pb-cyan/50 bg-pb-cyan/5"}`}
              data-testid="sports-card-feature-row">
              <span className={`text-[11px] font-semibold ${isFeatured ? "text-amber-300" : "text-zinc-300"}`}>
                {isFeatured ? "This card is on your Player Profile" : "Want this card on your Player Profile instead of your regular player card?"}
              </span>
              <button data-testid="sports-card-feature-btn" onClick={toggleFeature} disabled={featBusy}
                className={`shrink-0 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-2 transition-all active:scale-95 disabled:opacity-50 ${
                  isFeatured ? "border border-pb-border text-zinc-300 hover:border-red-500 hover:text-red-400" : "bg-pb-cyan text-black hover:bg-[#BEF264]"}`}>
                {featBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
                {isFeatured ? "Remove" : "Set as Profile Card"}
              </button>
            </div>
            <div className="flex gap-2 mt-3">
              <button data-testid="sports-card-share-btn" onClick={share} disabled={sharing}
                className="flex-1 flex items-center justify-center gap-2 bg-pb-red text-black font-black uppercase tracking-widest px-4 py-2.5 text-xs hover:bg-[#BEF264] transition-colors disabled:opacity-50">
                {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />} Share to Feed
              </button>
              <a data-testid="sports-card-download-btn" href={viewer.url} download="pickle-bullpen-card.png"
                className="flex items-center justify-center gap-2 border border-pb-border text-zinc-300 px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:border-pb-cyan hover:text-pb-cyan transition-colors">
                <Download className="w-3.5 h-3.5" /> Save
              </a>
              <button data-testid="sports-card-viewer-close" onClick={() => setViewer(null)}
                className="border border-pb-border text-zinc-300 px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:border-red-500 hover:text-red-400">
                Close
              </button>
            </div>
            <div className="flex justify-center mt-3 border border-pb-border py-2.5 bg-pb-surface">
              <ShareMenu url={`${process.env.REACT_APP_BACKEND_URL}/api/share/cards/${viewer.id}`}
                text="Check out my AI Sports Card from the Pickle Bullpen! 🏓" image={viewer.url}
                testId="sports-card-social-share" label="Share to Socials" />
            </div>
          </div>
        </div>
      )}
      </div>
      )}
    </div>
  );
}
